import type { QueryClient } from "@tanstack/react-query";
import { localDateStr } from "./dateUtils";
import { getJson, queryClient } from "./queryClient";

/**
 * The queries that read the Your days record (backend services/app_days.py):
 * Progress → Your days / Last 7 days, the My Day flame (habit-day streak),
 * the Progress overview and the year heatmap.
 *
 * Each sends the phone's own date as `localDate` (review 2026-10-14): check-
 * offs are stamped with the phone's day, but the server's "today" came from
 * the stored timezone, which only re-syncs now and then — someone travelling
 * saw today's check-off missing, or yesterday counted as missed. The server
 * uses it when it is within a day of its own view.
 *
 * The date sits in the query KEY as a second element, so the record rolls
 * over at midnight (a new key on the next render) while the first element
 * stays the plain URL every invalidation already uses — `invalidateQueries`
 * matches by prefix.
 */
export const APP_DAYS_URL = "/api/analytics/app-days?days=30";
export const HABIT_DAY_STREAK_URL = "/api/analytics/habit-day-streak";
export const OVERVIEW_URL = "/api/analytics/overview";
export const YEAR_HEATMAP_URL = "/api/analytics/heatmap?weeks=52";
/** Rest days for the streaks the phone counts itself (lib/streak.ts) — the
 *  same ten years its walk reads (MAX_WALK_DAYS), so an old rest day is never
 *  mistaken for a miss. */
export const REST_DAYS_URL = "/api/streaks/rest-days?days=3660";

export function withLocalDate(url: string, localDate: string): string {
  return `${url}${url.includes("?") ? "&" : "?"}localDate=${encodeURIComponent(localDate)}`;
}

/** The most recent saved answer for `url` under any date — yesterday's record
 *  restored from the saved cache on the first open of a day. */
export function newestDayRecord<T>(url: string): T | undefined {
  let best: { date: string; data: T } | undefined;
  for (const [key, data] of queryClient.getQueriesData<T>({ queryKey: [url] })) {
    const date = (key[1] as { localDate?: string } | undefined)?.localDate ?? "";
    if (data !== undefined && (!best || date > best.date)) best = { date, data };
  }
  return best?.data;
}

/** `{ queryKey, queryFn, placeholderData }` for one of the day-record queries.
 *
 *  The date in the key means the first open of each day (and the midnight
 *  rollover) has no cached answer, so the cards went blank / to skeletons on
 *  the most common open (review 2026-10-15). Until today's answer lands they
 *  show the previous one — the last answer this screen had, or the newest
 *  saved one — then swap in place. */
export function dayRecordQuery<T>(url: string, localDate: string = localDateStr()) {
  return {
    queryKey: [url, { localDate }] as const,
    queryFn: () => getJson<T>(withLocalDate(url, localDate)),
    placeholderData: (previous: T | undefined) => previous ?? newestDayRecord<T>(url),
  };
}

/** Refresh everything that reads the day record — call after anything that
 *  changes a day: a check-off from any screen, an un-check, a day off or a
 *  habit's "Not today", the honesty check-in. */
export function invalidateDayRecord(queryClient: QueryClient): void {
  // The workout run is held by the same days off, so a day off refreshes it.
  for (const url of [APP_DAYS_URL, HABIT_DAY_STREAK_URL, OVERVIEW_URL, YEAR_HEATMAP_URL, REST_DAYS_URL, "/api/workouts/streak"]) {
    queryClient.invalidateQueries({ queryKey: [url] });
  }
}
